import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, AlertCircle, Heart, Utensils } from 'lucide-react';
import DonateBtn from './DonateBtn';

export default function DailyFeeding() {
  
  // Exact Image Links mapped to your provided order
  const photoMain = "/images/daily.jpg"; 
  const photoLeft = "/images/daily2.jpg";
  const photoRight = "/images/fd 2.jpg";

  const impactPoints = [
    { icon: <ChefHat className="text-gh-blue" size={20} />, text: "Nutritious, reliable daily meals." },
    { icon: <Utensils className="text-gh-blue" size={20} />, text: "Strength for their bodies and minds." },
    { icon: <Heart className="text-gh-blue" size={20} />, text: "Hope that tomorrow will be kind." },
  ];

  return (
    <section id="daily-feeding-section" className="py-24 bg-[#f4f7f9] overflow-hidden border-y border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* LEFT CONTENT: THE STORY */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#F6DBCB] text-[#8C3F2B] px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-[#E27D60]/20">
              <AlertCircle size={16} /> Immediate Action Required
            </div>
            
            <h2 className="text-4xl md:text-6xl font-black text-gh-blue italic uppercase tracking-tighter leading-[0.9] mb-8">
              Daily Hunger, <br />
              <span className="text-[#E27D60]">Daily Hope</span>
            </h2>

            <div className="space-y-6 text-slate-600 leading-relaxed text-lg mb-10">
              <p className="font-bold text-gh-blue italic border-l-4 border-gh-orange pl-4 bg-white/50 py-2 rounded-r-xl">
                "Hunger isn't just a physical pain—it is a daily shadow."
              </p>
              <p>
                Every single day, many of our rescued children wake up not knowing if they will eat. Hunger is their daily struggle and even one meal is never guaranteed. Some go to bed with empty stomachs, hoping tomorrow will be better.
              </p>
              <p>
                Just <span className="text-gh-blue font-bold">$50</span> can feed them for a day. Please if you feel it in your heart, stand with us—any small gift can put food on their plates, strength in their bodies, and hope in their hearts.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
              <h4 className="font-black text-gh-blue uppercase italic text-sm mb-4">What your support provides:</h4>
              <div className="space-y-4">
                {impactPoints.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                    {item.icon} {item.text}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* RIGHT CONTENT: THE VISUAL GRID */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4 rounded-[3rem] overflow-hidden relative z-10">
              {/* Top Main Image */}
              <img 
                src={photoMain} 
                alt="Giving Hands Mission" 
                className="w-full h-80 object-cover rounded-3xl col-span-2 border-4 border-white shadow-md bg-slate-200"
              />
              {/* Bottom Supporting Images */}
              <img 
                src={photoLeft} 
                alt="Children Community" 
                className="w-full h-48 object-cover rounded-3xl border-4 border-white shadow-md bg-slate-200"
              />
              <img 
                src={photoRight} 
                alt="Meal Support" 
                className="w-full h-48 object-cover rounded-3xl border-4 border-white shadow-md bg-slate-200"
              />
            </div>

            {/* ACTION CARD */}
            <div className="absolute -bottom-10 -left-6 z-20 w-72 md:w-80">
              <div className="bg-[#E27D60] p-8 rounded-[2.5rem] border-[6px] border-white shadow-2xl text-center text-white">
                <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart size={24} fill="white" />
                </div>
                <p className="text-4xl font-black mb-2 tracking-tighter">$50</p>
                <p className="text-xs font-bold leading-relaxed mb-6 uppercase tracking-widest text-white/90">
                  Feeds all our kids <br/>for an entire day
                </p>
                
                <DonateBtn 
                  label="Feed Them Today" 
                  className="w-full !py-4 !bg-gh-blue hover:!bg-white !text-white hover:!text-gh-blue !rounded-2xl !text-xs !font-black uppercase tracking-widest transition-all shadow-xl"
                />
              </div>
            </div>

            {/* Subtle Blue Blur background */}
            <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-gh-blue/5 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}